import type { Stage } from "./Stage0Onramp";
import { BlueprintPane } from "../components/BlueprintPane";
import { ChatPane } from "../components/ChatPane";
import type { BlueprintUpdateEvent, Session, StageChangeEvent } from "../api/types";

export class Stage2Drilldown implements Stage {
  private sessionId: string;
  private onStageChange: (stage: number) => void;
  private existingSession: Session | null;
  private resuming: boolean;
  private onStartOver: () => void;
  private chatPane: ChatPane | null = null;
  private blueprintPane: BlueprintPane | null = null;

  constructor(sessionId: string, onStageChange: (stage: number) => void, existingSession: Session | null = null, resuming = false, onStartOver: () => void = () => {}) {
    this.sessionId = sessionId;
    this.onStageChange = onStageChange;
    this.existingSession = existingSession;
    this.resuming = resuming;
    this.onStartOver = onStartOver;
  }

  mount(container: HTMLElement): void {
    container.innerHTML = `
      <div class="progress-header" role="progressbar" aria-valuenow="60" aria-valuemin="0" aria-valuemax="100" aria-label="Session progress: step 3 of 5">
        <div class="progress-steps">
          <span>Step 3 of 5 — Drill-down</span>
          <div class="progress-bar"><div class="progress-fill" style="width:60%"></div></div>
        </div>
        <div class="header-right">
          <span class="time-est">~5 minutes left</span>
          <button class="btn-ghost btn-start-over" type="button" aria-label="Discard this session and start a new assessment">Start over</button>
        </div>
      </div>
      <div class="split">
        <div class="pane chat-pane"></div>
        <div class="pane blueprint pane-blueprint"></div>
      </div>
    `;

    const chatEl = container.querySelector<HTMLElement>(".chat-pane")!;
    const bpEl = container.querySelector<HTMLElement>(".pane-blueprint")!;

    this.blueprintPane = new BlueprintPane(bpEl);
    this.chatPane = new ChatPane(chatEl, {
      sessionId: this.sessionId,
      onBlueprintUpdate: (e: BlueprintUpdateEvent) => this.blueprintPane?.update(e.data),
      onStageChange: (e: StageChangeEvent) => this.onStageChange(e.stage),
      onError: (msg) => console.error("Chat error:", msg),
    });

    if (this.resuming && (this.existingSession?.conversation ?? []).length > 0) {
      // Page reload — replay full saved history
      this.chatPane.restoreHistory(this.existingSession!.conversation);
      if (this.existingSession?.blueprint_state) {
        this.blueprintPane.update(this.existingSession.blueprint_state);
      }
    } else {
      // Fresh start or live transition — restore blueprint state if available, then kick off
      if (this.existingSession?.blueprint_state) {
        this.blueprintPane.update(this.existingSession.blueprint_state);
      }
      this.chatPane.kickOff();
    }

    container.querySelector<HTMLButtonElement>(".btn-start-over")?.addEventListener("click", () => {
      if (window.confirm("Start a new assessment? Your current session will be lost.")) {
        this.onStartOver();
      }
    });
  }

  unmount(): void {
    this.chatPane?.unmount();
  }
}
